import { FEATURE_KEYS } from './featureConstants';

export type BusinessType = 
  | 'pharmacy' 
  | 'restaurant' 
  | 'bar' 
  | 'retail' 
  | 'general' 
  | 'hospital' 
  | 'coffee_shop' 
  | 'wholesale'
  | 'distributor'
  | 'shoe' 
  | 'clothing' 
  | 'hardware' 
  | 'grocery' 
  | 'jewelry' 
  | 'hotel'
  | 'mobile_money'
  | 'salon'
  | 'barbershop'
  | 'real_estate'
  | 'spa'
  | 'tailoring'
  | 'photography'
  | 'car_rental'
  | 'freelancer';

export const getBusinessType = (type?: string | number): BusinessType => {
  const normalizedType = String(type ?? '').toLowerCase().trim();
  
  if (
    normalizedType === '219' ||
    normalizedType.includes('real_estate') ||
    normalizedType.includes('realestate') ||
    normalizedType.includes('real estate') ||
    normalizedType.includes('property') ||
    normalizedType.includes('immobilier')
  ) return 'real_estate';

  if (
    normalizedType === '218' ||
    normalizedType.includes('barber') ||
    normalizedType.includes('barbier') ||
    normalizedType.includes('grooming')
  ) return 'barbershop';

  if (
    normalizedType === '220' ||
    (normalizedType.includes('spa') && !normalizedType.includes('salon_spa')) ||
    normalizedType.includes('massage') ||
    normalizedType.includes('wellness') ||
    normalizedType.includes('sauna')
  ) return 'spa';

  if (
    normalizedType === '217' ||
    normalizedType.includes('salon') ||
    normalizedType.includes('coiffure') ||
    normalizedType.includes('hairstyling') ||
    normalizedType.includes('hair') ||
    normalizedType.includes('beauty') ||
    normalizedType.includes('aesthetic')
  ) return 'salon';

  if (
    normalizedType === '221' ||
    normalizedType.includes('tailor') ||
    normalizedType.includes('couture') ||
    normalizedType.includes('couturier') ||
    normalizedType.includes('bespoke')
  ) return 'tailoring';

  if (
    normalizedType === '222' ||
    normalizedType.includes('car_rental') ||
    normalizedType.includes('car rental') ||
    normalizedType.includes('vehicle') ||
    normalizedType.includes('fleet') ||
    normalizedType.includes('location voiture')
  ) return 'car_rental';

  if (normalizedType === '223' || normalizedType.includes('photo') || normalizedType.includes('photography') || normalizedType.includes('studio')) return 'photography';

  if (
    normalizedType === '227' ||
    normalizedType.includes('freelanc') ||
    normalizedType.includes('professional_services') ||
    normalizedType.includes('professional services') ||
    normalizedType.includes('consult') ||
    normalizedType.includes('advisory') ||
    normalizedType.includes('services')
  ) return 'freelancer';

  if (
    normalizedType.includes('hotel') ||
    normalizedType.includes('hostel') ||
    normalizedType.includes('hospitality') ||
    normalizedType.includes('lodge') ||
    normalizedType.includes('lodging') ||
    normalizedType.includes('resort') ||
    normalizedType.includes('motel') ||
    normalizedType.includes('guesthouse') ||
    normalizedType.includes('guest_house') ||
    normalizedType.includes('auberge') ||
    normalizedType.includes('inn') ||
    normalizedType.includes('hebergement') ||
    normalizedType.includes('hébergement')
  ) return 'hotel';

  if (normalizedType.includes('hospital') || normalizedType.includes('clinic')) return 'hospital';
  if (normalizedType.includes('pharmacy')) return 'pharmacy';
  if (
    normalizedType.includes('mobile_money') ||
    normalizedType.includes('mobile money') ||
    normalizedType.includes('agency_banking') ||
    normalizedType.includes('agency banking') ||
    normalizedType.includes('momo') ||
    normalizedType.includes('airtime') ||
    normalizedType.includes('financier') ||
    normalizedType.includes('financial')
  ) return 'mobile_money';
  if (normalizedType.includes('shoe') || normalizedType.includes('footwear') || normalizedType.includes('shoes')) return 'shoe';
  if (
    normalizedType.includes('fashion') ||
    normalizedType.includes('clothing') ||
    normalizedType.includes('garment') ||
    normalizedType.includes('apparel') ||
    normalizedType.includes('textile')
  ) return 'clothing';
  if (normalizedType.includes('hardware') || normalizedType.includes('building') || normalizedType.includes('tools')) return 'hardware';
  if (normalizedType.includes('grocery') || normalizedType.includes('supermarket')) return 'grocery';
  if (normalizedType.includes('jewelry') || normalizedType.includes('gem')) return 'jewelry';
  if (normalizedType.includes('wholesale') || normalizedType.includes('grossiste')) return 'wholesale';
  if (normalizedType.includes('distribut')) return 'distributor';
  if (normalizedType.includes('cafe') || normalizedType.includes('coffee')) return 'coffee_shop';
  if (normalizedType.includes('restaurant') || normalizedType.includes('kitchen') || normalizedType.includes('food')) return 'restaurant';
  if (normalizedType.includes('bar') || normalizedType.includes('club') || normalizedType.includes('lounge') || normalizedType.includes('drinks') || normalizedType.includes('snack_bar')) return 'bar';
  if (
    normalizedType.includes('retail') ||
    normalizedType.includes('store') ||
    normalizedType.includes('shop') ||
    normalizedType.includes('boutique') ||
    normalizedType.includes('electronics')
  ) return 'retail';
  
  return 'general';
};

export const isUserAdminOrOwner = (userData: any): boolean => {
  if (!userData) return false;
  if (userData.is_super_admin === true) return true;
  if (userData.is_admin === true || userData.user?.is_admin === true) return true;

  const roleStr = String(
    userData.role ||
    userData.user_role ||
    userData.user_role_name ||
    userData.userRole ||
    userData.user?.role ||
    ''
  ).toUpperCase();

  const rolesList = Array.isArray(userData.user_roles)
    ? userData.user_roles.map((r: any) => String(r).toUpperCase())
    : [roleStr];

  // 🛡️ Strict Check: ONLY Master Shop Owners / Master Admins can switch branches.
  // Branch Admins, Branch Managers, Cashiers, Tellers, Agents are locked to their assigned branch.
  return rolesList.some((r: string) => {
    const cleanRole = (r || '').trim();
    if (cleanRole === 'BRANCH_ADMIN' || cleanRole.includes('BRANCH ADMIN')) {
      return false;
    }
    return (
      cleanRole === 'OWNER' ||
      cleanRole === 'ADMIN' ||
      cleanRole === 'SUPER_ADMIN' ||
      cleanRole === 'SHOP_ADMIN' ||
      cleanRole.includes('ADMINISTRATOR') ||
      cleanRole.includes('OWNER')
    );
  });
};

export const ROLE_VISIBILITY: Record<BusinessType, string[]> = {
  hospital: [
    'Medical Doctor / Physician', 'Clinical Nurse', 'Pharmacist', 'Laboratory Technician', 'Clinic / Hospital Administrator', 'Receptionist', 'Admin', 'Owner',
    'Doctor', 'Physician', 'Nurse', 'Triage Nurse', 'Clinical Specialist', 'Hospital Administrator', 'HRM Manager', 'Cashier', 'Stock Manager', 'Administrator'
  ],
  pharmacy: [
    'Chief Pharmacist', 'Clinical Pharmacist', 'Pharmacy Technician', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager'
  ],
  restaurant: [
    'Restaurant Manager', 'Chef / Kitchen Staff', 'Waitstaff / Server', 'Bartender', 'Host / Hostess', 'HRM Manager', 'Administrator', 'Cashier', 'Inventory Clerk', 'Stock Manager'
  ],
  bar: [
    'Bar Manager', 'Bartender', 'Waitstaff / Server', 'Chef / Kitchen Staff', 'HRM Manager', 'Administrator', 'Cashier', 'Inventory Clerk', 'Stock Manager'
  ],
  coffee_shop: [
    'Coffee Shop Manager', 'Barista', 'Waiter', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager'
  ],
  wholesale: [
    'Wholesale Manager', 'Sales Representative', 'Warehouse Supervisor', 'Logistics Coordinator', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Admin', 'Owner'
  ],
  distributor: [
    'Distribution Manager', 'Fleet & Transport Supervisor', 'Depot Dispatcher', 'Logistics Coordinator', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Admin', 'Owner'
  ],
  shoe: [
    'Shop Manager', 'Store Manager', 'Sales Associate', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Inventory Clerk', 'Admin', 'Owner'
  ],
  clothing: [
    'Shop Manager', 'Store Manager', 'Sales Associate', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Inventory Clerk', 'Admin', 'Owner'
  ],
  hardware: [
    'Hardware Store Manager', 'Spool & Inventory Specialist', 'Equipment Rental Specialist', 'Contractor Credit Manager', 'Hardware Cashier', 'Shop Manager', 'Store Manager', 'Sales Associate', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Inventory Clerk', 'Admin', 'Owner'
  ],
  grocery: [
    'Shop Manager', 'Store Manager', 'Sales Associate', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Inventory Clerk', 'Admin', 'Owner'
  ],
  jewelry: [
    'Shop Manager', 'Store Manager', 'Sales Associate', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Inventory Clerk', 'Admin', 'Owner'
  ],
  hotel: [
    'Hotel General Manager', 'Hostel Manager', 'Hostel Administrator', 'Front Desk Agent', 'Reservations Coordinator', 'Head Housekeeper', 'Room Attendant', 'Night Auditor', 'Shop Manager', 'Administrator', 'Admin', 'Owner'
  ],
  mobile_money: [
    'Mobile Money Super Admin', 'Mobile Money Branch Manager', 'Mobile Money Teller / Agent Operator', 'Mobile Money Auditor / Accountant', 'Mobile Money Agent', 'Teller / Kiosk Operator', 'Agency Banking Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Admin', 'Owner'
  ],
  retail: [
    'Shop Manager', 'Store Manager', 'Supermarket Manager', 'Sales Associate', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Inventory Clerk', 'Owner'
  ],
  salon: [
    'Salon Manager', 'Master Colorist', 'Hair Stylist', 'Beauty & Makeup Artist', 'Salon Receptionist', 'Colorist', 'Esthetician', 'Makeup Artist', 'Receptionist', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  barbershop: [
    'Barbershop Manager', 'Master Barber', 'Junior Barber', 'Front Desk / Receptionist', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  real_estate: [
    'Managing Broker', 'Property Manager', 'Real Estate Agent', 'Leasing Coordinator', 'Maintenance Supervisor', 'Listing Coordinator', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  spa: [
    'Spa Director', 'Massage Therapist', 'Esthetician', 'Wellness Consultant', 'Spa Receptionist', 'Receptionist', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  tailoring: [
    'Atelier Director', 'Master Tailor', 'Seamstress / Patternmaker', 'Fitting Specialist', 'Apprentice', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  car_rental: [
    'Fleet Manager', 'Rental Desk Agent', 'Vehicle Inspection Specialist', 'Fleet Maintenance Coordinator', 'Rental Agent', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  photography: [
    'Studio Manager', 'Lead Photographer', 'Photo Editor', 'Assistant', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  freelancer: [
    'Managing Partner', 'Senior Consultant', 'Project Manager', 'Billing & Practice Coordinator', 'Independent Professional', 'Consultant', 'Creator', 'HRM Manager', 'Administrator', 'Owner', 'Admin'
  ],
  general: [
    'Shop Manager', 'Store Manager', 'Supermarket Manager', 'Sales Associate', 'HRM Manager', 'Administrator', 'Cashier', 'Stock Manager', 'Inventory Clerk', 'Manager', 'Staff', 'Owner'
  ]
};

// ============================================================================
// ROLE → FEATURE MAP
// Defines exactly which feature keys each role is allowed to see.
// If a role is NOT in this map, the user sees everything their permissions allow (manager/admin).
// ============================================================================
export const ROLE_FEATURE_MAP: Record<string, number[]> = {

  // --- Mobile Money / Agent Banking ---
  'Mobile Money Super Admin': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.SETTINGS,
    FEATURE_KEYS.FINANCIAL_ANALYTICS,
    FEATURE_KEYS.HRM,
    FEATURE_KEYS.PAYROLL,
    FEATURE_KEYS.MOBILE_MONEY_POS_OPS,
    FEATURE_KEYS.MOBILE_MONEY_SHIFT_RECONCILIATION,
    FEATURE_KEYS.MOBILE_MONEY_FLOAT_MGMT,
    FEATURE_KEYS.MOBILE_MONEY_FINANCIAL_ANALYTICS,
    FEATURE_KEYS.MOBILE_MONEY_EXPENSES_OUTFLOWS,
    FEATURE_KEYS.MOBILE_MONEY_TELLER_MGMT,
    FEATURE_KEYS.MOBILE_MONEY_SETTINGS_COMMISSIONS,
  ],
  'Mobile Money Branch Manager': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.SALES_ANALYTICS,
    FEATURE_KEYS.FINANCIAL_ANALYTICS,
    FEATURE_KEYS.HRM,
    FEATURE_KEYS.MOBILE_MONEY_POS_OPS,
    FEATURE_KEYS.MOBILE_MONEY_SHIFT_RECONCILIATION,
    FEATURE_KEYS.MOBILE_MONEY_FLOAT_MGMT,
    FEATURE_KEYS.MOBILE_MONEY_FINANCIAL_ANALYTICS,
    FEATURE_KEYS.MOBILE_MONEY_EXPENSES_OUTFLOWS,
    FEATURE_KEYS.MOBILE_MONEY_TELLER_MGMT,
  ],
  'Mobile Money Teller / Agent Operator': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.MOBILE_MONEY_POS_OPS,
    FEATURE_KEYS.MOBILE_MONEY_SHIFT_RECONCILIATION,
    FEATURE_KEYS.MOBILE_MONEY_EXPENSES_OUTFLOWS,
  ],
  'Mobile Money Auditor / Accountant': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.SALES_ANALYTICS,
    FEATURE_KEYS.FINANCIAL_ANALYTICS,
    FEATURE_KEYS.MOBILE_MONEY_SHIFT_RECONCILIATION,
    FEATURE_KEYS.MOBILE_MONEY_FINANCIAL_ANALYTICS,
    FEATURE_KEYS.MOBILE_MONEY_EXPENSES_OUTFLOWS,
  ],

  // --- Bar & Restaurant ---
  'Waitstaff / Server': [
    FEATURE_KEYS.RESTAURANT_TABLES,
    FEATURE_KEYS.RESTAURANT_RESERVATIONS,
    FEATURE_KEYS.BAR_TABS,
    FEATURE_KEYS.BAR_POS,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.RESTAURANT_DASHBOARD,
  ],
  'Host / Hostess': [
    FEATURE_KEYS.RESTAURANT_TABLES,
    FEATURE_KEYS.RESTAURANT_RESERVATIONS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Chef / Kitchen Staff': [
    FEATURE_KEYS.RESTAURANT_KITCHEN,
    FEATURE_KEYS.BAR_MENUS,
    FEATURE_KEYS.BAR_RECIPES,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.RESTAURANT_DASHBOARD,
  ],
  'Bartender': [
    FEATURE_KEYS.BAR_POS,
    FEATURE_KEYS.BAR_TABS,
    FEATURE_KEYS.BAR_STOCK,
    FEATURE_KEYS.BAR_MOVEMENTS,
    FEATURE_KEYS.BAR_SHIFTS,
    FEATURE_KEYS.COMPTOIRS,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.BAR_DASHBOARD,
  ],
  'Barista': [
    FEATURE_KEYS.BAR_POS,
    FEATURE_KEYS.BAR_TABS,
    FEATURE_KEYS.BAR_STOCK,
    FEATURE_KEYS.BAR_MOVEMENTS,
    FEATURE_KEYS.BAR_SHIFTS,
    FEATURE_KEYS.BAR_RECIPES,
    FEATURE_KEYS.BAR_MENUS,
    FEATURE_KEYS.RESTAURANT_KITCHEN,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.RESTAURANT_DASHBOARD,
  ],
  'Waiter': [
    FEATURE_KEYS.RESTAURANT_TABLES,
    FEATURE_KEYS.RESTAURANT_RESERVATIONS,
    FEATURE_KEYS.BAR_TABS,
    FEATURE_KEYS.BAR_POS,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.RESTAURANT_DASHBOARD,
  ],
  'Coffee Shop Manager': [],

  // --- Pharmacy ---
  'Chief Pharmacist': [], // Empty = no restriction, sees all pharmacy features
  'Clinical Pharmacist': [
    FEATURE_KEYS.PHARMACY_POS,
    FEATURE_KEYS.DISPENSING,
    FEATURE_KEYS.PRESCRIPTIONS,
    FEATURE_KEYS.MEDICATIONS,
    FEATURE_KEYS.PATIENTS,
    FEATURE_KEYS.PHARMACY_BATCHES,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.PHARMACY_DASHBOARD,
  ],
  'Pharmacist': [
    FEATURE_KEYS.PHARMACY_POS,
    FEATURE_KEYS.DISPENSING,
    FEATURE_KEYS.PRESCRIPTIONS,
    FEATURE_KEYS.MEDICATIONS,
    FEATURE_KEYS.PATIENTS,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.PHARMACY_DASHBOARD,
  ],
  'Pharmacy Technician': [
    FEATURE_KEYS.PHARMACY_POS,
    FEATURE_KEYS.DISPENSING,
    FEATURE_KEYS.MEDICATIONS,
    FEATURE_KEYS.PHARMACY_BATCHES,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Retail / Shop ---
  'Sales Associate': [
    FEATURE_KEYS.POS_V2,
    FEATURE_KEYS.SALES,
    FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Inventory Clerk': [
    FEATURE_KEYS.PRODUCTS,
    FEATURE_KEYS.INVENTORY,
    FEATURE_KEYS.STOCK_MOVEMENTS,
    FEATURE_KEYS.CATEGORIES,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Hardware Store Roles ---
  'Hardware Cashier': [
    FEATURE_KEYS.POS_V2,
    FEATURE_KEYS.HARDWARE_DASHBOARD,
    FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.SALES_HISTORY,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Spool & Inventory Specialist': [
    FEATURE_KEYS.PRODUCTS,
    FEATURE_KEYS.INVENTORY,
    FEATURE_KEYS.CATEGORIES,
    FEATURE_KEYS.STOCK_MOVEMENTS,
    FEATURE_KEYS.SUPPLIERS,
    FEATURE_KEYS.HARDWARE_DASHBOARD,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Equipment Rental Specialist': [
    FEATURE_KEYS.HARDWARE_DASHBOARD,
    FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Contractor Credit Manager': [
    FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.DEBTS,
    FEATURE_KEYS.HARDWARE_DASHBOARD,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Mobile Money & Agency Banking Roles ---
  'Mobile Money Agent': [
    FEATURE_KEYS.MOBILE_MONEY_DASHBOARD,
    FEATURE_KEYS.MOBILE_MONEY_POS,
    FEATURE_KEYS.MOBILE_MONEY_FLOATS,
    FEATURE_KEYS.MOBILE_MONEY_LEDGER,
    FEATURE_KEYS.MOBILE_MONEY_RECONCILIATION,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Teller / Kiosk Operator': [
    FEATURE_KEYS.MOBILE_MONEY_POS,
    FEATURE_KEYS.MOBILE_MONEY_LEDGER,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Agency Banking Manager': [
    FEATURE_KEYS.MOBILE_MONEY_DASHBOARD,
    FEATURE_KEYS.MOBILE_MONEY_POS,
    FEATURE_KEYS.MOBILE_MONEY_FLOATS,
    FEATURE_KEYS.MOBILE_MONEY_LEDGER,
    FEATURE_KEYS.MOBILE_MONEY_RECONCILIATION,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Cross-business Cashier (restricted to POS only) ---
  'Cashier': [
    FEATURE_KEYS.POS_V2,
    FEATURE_KEYS.BAR_POS,
    FEATURE_KEYS.PHARMACY_POS,
    FEATURE_KEYS.SALES,
    FEATURE_KEYS.BAR_TABS,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Cross-business Stock Manager ---
  'Stock Manager': [
    FEATURE_KEYS.PRODUCTS,
    FEATURE_KEYS.INVENTORY,
    FEATURE_KEYS.STOCK_MOVEMENTS,
    FEATURE_KEYS.CATEGORIES,
    FEATURE_KEYS.BAR_STOCK,
    FEATURE_KEYS.BAR_MOVEMENTS,
    FEATURE_KEYS.MEDICATIONS,
    FEATURE_KEYS.PHARMACY_BATCHES,
    FEATURE_KEYS.PROCUREMENT,
    FEATURE_KEYS.PURCHASE_ORDERS,
    FEATURE_KEYS.DELIVERY_NOTES,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Hospital Clinical Roles ---
  'Doctor': [
    FEATURE_KEYS.HOSPITAL_DASHBOARD,
    FEATURE_KEYS.CLINICAL_QUEUE,
    FEATURE_KEYS.CONSULTATION,
    FEATURE_KEYS.PATIENTS,
    FEATURE_KEYS.MEDICATIONS,
    FEATURE_KEYS.PRESCRIPTIONS,
    FEATURE_KEYS.HOSPITAL_LABS,
    FEATURE_KEYS.HOSPITAL_WARDS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Physician': [
    FEATURE_KEYS.HOSPITAL_DASHBOARD,
    FEATURE_KEYS.CLINICAL_QUEUE,
    FEATURE_KEYS.CONSULTATION,
    FEATURE_KEYS.PATIENTS,
    FEATURE_KEYS.MEDICATIONS,
    FEATURE_KEYS.PRESCRIPTIONS,
    FEATURE_KEYS.HOSPITAL_LABS,
    FEATURE_KEYS.HOSPITAL_WARDS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Clinical Specialist': [
    FEATURE_KEYS.HOSPITAL_DASHBOARD,
    FEATURE_KEYS.CLINICAL_QUEUE,
    FEATURE_KEYS.CONSULTATION,
    FEATURE_KEYS.PATIENTS,
    FEATURE_KEYS.MEDICATIONS,
    FEATURE_KEYS.PRESCRIPTIONS,
    FEATURE_KEYS.HOSPITAL_LABS,
    FEATURE_KEYS.HOSPITAL_WARDS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Nurse': [
    FEATURE_KEYS.HOSPITAL_DASHBOARD,
    FEATURE_KEYS.CLINICAL_QUEUE,
    FEATURE_KEYS.TRIAGE,
    FEATURE_KEYS.PATIENTS,
    FEATURE_KEYS.HOSPITAL_WARDS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Triage Nurse': [
    FEATURE_KEYS.HOSPITAL_DASHBOARD,
    FEATURE_KEYS.CLINICAL_QUEUE,
    FEATURE_KEYS.TRIAGE,
    FEATURE_KEYS.PATIENTS,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Hotel PMS Roles ---
  'Hotel General Manager': [
    FEATURE_KEYS.HOTEL_DASHBOARD,
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_RESERVATIONS,
    FEATURE_KEYS.HOTEL_HOUSEKEEPING,
    FEATURE_KEYS.HOTEL_FOLIOS,
    FEATURE_KEYS.HOTEL_REPORTS,
    FEATURE_KEYS.HOTEL_GUESTS,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.REPORTS,
    FEATURE_KEYS.SETTINGS,
  ],
  'Hostel Manager': [
    FEATURE_KEYS.HOTEL_DASHBOARD,
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_RESERVATIONS,
    FEATURE_KEYS.HOTEL_HOUSEKEEPING,
    FEATURE_KEYS.HOTEL_FOLIOS,
    FEATURE_KEYS.HOTEL_REPORTS,
    FEATURE_KEYS.HOTEL_GUESTS,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.REPORTS,
    FEATURE_KEYS.SETTINGS,
  ],
  'Hostel Administrator': [
    FEATURE_KEYS.HOTEL_DASHBOARD,
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_RESERVATIONS,
    FEATURE_KEYS.HOTEL_HOUSEKEEPING,
    FEATURE_KEYS.HOTEL_FOLIOS,
    FEATURE_KEYS.HOTEL_REPORTS,
    FEATURE_KEYS.HOTEL_GUESTS,
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.REPORTS,
    FEATURE_KEYS.SETTINGS,
  ],
  'Front Desk Agent': [
    FEATURE_KEYS.HOTEL_DASHBOARD,
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_RESERVATIONS,
    FEATURE_KEYS.HOTEL_FOLIOS,
    FEATURE_KEYS.HOTEL_GUESTS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Reservations Coordinator': [
    FEATURE_KEYS.HOTEL_DASHBOARD,
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_RESERVATIONS,
    FEATURE_KEYS.HOTEL_GUESTS,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Head Housekeeper': [
    FEATURE_KEYS.HOTEL_DASHBOARD,
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_HOUSEKEEPING,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Room Attendant': [
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_HOUSEKEEPING,
    FEATURE_KEYS.DASHBOARD,
  ],
  'Night Auditor': [
    FEATURE_KEYS.HOTEL_DASHBOARD,
    FEATURE_KEYS.HOTEL_ROOMS,
    FEATURE_KEYS.HOTEL_RESERVATIONS,
    FEATURE_KEYS.HOTEL_FOLIOS,
    FEATURE_KEYS.HOTEL_REPORTS,
    FEATURE_KEYS.DASHBOARD,
  ],

  // --- Real Estate & Property ---
  'Managing Broker': [],
  'Property Manager': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_PROPERTIES,
    FEATURE_KEYS.REAL_ESTATE_UNITS,
    FEATURE_KEYS.REAL_ESTATE_RENT,
    FEATURE_KEYS.REAL_ESTATE_MAINTENANCE,
    FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.REPORTS,
  ],
  'Real Estate Agent': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_PROPERTIES,
    FEATURE_KEYS.REAL_ESTATE_UNITS,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Leasing Coordinator': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_UNITS,
    FEATURE_KEYS.REAL_ESTATE_RENT,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Maintenance Supervisor': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_DASHBOARD,
    FEATURE_KEYS.REAL_ESTATE_PROPERTIES,
    FEATURE_KEYS.REAL_ESTATE_MAINTENANCE,
    FEATURE_KEYS.EXPENSES,
  ],

  // --- Barbershop & Grooming ---
  'Barbershop Manager': [],
  'Master Barber': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.BARBERSHOP_DASHBOARD,
    FEATURE_KEYS.BARBERSHOP_POS,
    FEATURE_KEYS.BARBERSHOP_STATIONS,
    FEATURE_KEYS.BARBERSHOP_CATALOG,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Junior Barber': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.BARBERSHOP_DASHBOARD,
    FEATURE_KEYS.BARBERSHOP_POS,
    FEATURE_KEYS.BARBERSHOP_STATIONS,
  ],
  'Front Desk / Receptionist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.BARBERSHOP_DASHBOARD,
    FEATURE_KEYS.BARBERSHOP_POS,
    FEATURE_KEYS.CUSTOMERS,
  ],

  // --- Spa & Wellness ---
  'Spa Director': [],
  'Massage Therapist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SPA_DASHBOARD,
    FEATURE_KEYS.SPA_BOOKINGS,
    FEATURE_KEYS.SPA_ROOMS,
  ],
  'Esthetician': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SPA_DASHBOARD,
    FEATURE_KEYS.SPA_BOOKINGS,
    FEATURE_KEYS.SPA_ROOMS,
  ],
  'Wellness Consultant': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SPA_DASHBOARD,
    FEATURE_KEYS.SPA_BOOKINGS,
    FEATURE_KEYS.SPA_MEMBERSHIPS,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Spa Receptionist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SPA_DASHBOARD,
    FEATURE_KEYS.SPA_BOOKINGS,
    FEATURE_KEYS.SPA_ROOMS,
    FEATURE_KEYS.SPA_MEMBERSHIPS,
    FEATURE_KEYS.CUSTOMERS,
  ],

  // --- Tailoring & Custom Clothing ---
  'Atelier Director': [],
  'Master Tailor': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.TAILORING_DASHBOARD,
    FEATURE_KEYS.TAILORING_ORDERS,
    FEATURE_KEYS.TAILORING_MEASUREMENTS,
    FEATURE_KEYS.TAILORING_FABRICS,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Seamstress / Patternmaker': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.TAILORING_DASHBOARD,
    FEATURE_KEYS.TAILORING_ORDERS,
    FEATURE_KEYS.TAILORING_MEASUREMENTS,
    FEATURE_KEYS.TAILORING_FABRICS,
  ],
  'Fitting Specialist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.TAILORING_DASHBOARD,
    FEATURE_KEYS.TAILORING_ORDERS,
    FEATURE_KEYS.TAILORING_MEASUREMENTS,
    FEATURE_KEYS.CUSTOMERS,
  ],

  // --- Professional Services & Consulting ---
  'Managing Partner': [],
  'Senior Consultant': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_DASHBOARD,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_ENGAGEMENTS,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_TIMESHEETS,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_RETAINERS,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Project Manager': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_DASHBOARD,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_ENGAGEMENTS,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_TIMESHEETS,
    FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.EXPENSES,
  ],
  'Billing & Practice Coordinator': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_DASHBOARD,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_TIMESHEETS,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_RETAINERS,
    FEATURE_KEYS.FUNDS,
    FEATURE_KEYS.DEBTS,
    FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.REPORTS,
  ],

  // --- Hair Salon & Beauty ---
  'Salon Manager': [],
  'Master Colorist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SALON_DASHBOARD,
    FEATURE_KEYS.SALON_APPOINTMENTS,
    FEATURE_KEYS.SALON_FORMULAS,
    FEATURE_KEYS.SALON_CHAIRS,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Hair Stylist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SALON_DASHBOARD,
    FEATURE_KEYS.SALON_APPOINTMENTS,
    FEATURE_KEYS.SALON_FORMULAS,
    FEATURE_KEYS.SALON_CHAIRS,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Beauty & Makeup Artist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SALON_DASHBOARD,
    FEATURE_KEYS.SALON_APPOINTMENTS,
    FEATURE_KEYS.SALON_CHAIRS,
  ],
  'Salon Receptionist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.SALON_DASHBOARD,
    FEATURE_KEYS.SALON_APPOINTMENTS,
    FEATURE_KEYS.SALON_CHAIRS,
    FEATURE_KEYS.CUSTOMERS,
  ],

  // --- Car Rental & Fleet ---
  'Fleet Manager': [],
  'Rental Desk Agent': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.CAR_RENTAL_DASHBOARD,
    FEATURE_KEYS.CAR_RENTAL_FLEET,
    FEATURE_KEYS.CAR_RENTAL_AGREEMENTS,
    FEATURE_KEYS.CUSTOMERS,
  ],
  'Vehicle Inspection Specialist': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.CAR_RENTAL_DASHBOARD,
    FEATURE_KEYS.CAR_RENTAL_FLEET,
    FEATURE_KEYS.CAR_RENTAL_AGREEMENTS,
    FEATURE_KEYS.CAR_RENTAL_DAMAGES,
  ],
  'Fleet Maintenance Coordinator': [
    FEATURE_KEYS.DASHBOARD,
    FEATURE_KEYS.CAR_RENTAL_DASHBOARD,
    FEATURE_KEYS.CAR_RENTAL_FLEET,
    FEATURE_KEYS.CAR_RENTAL_DAMAGES,
    FEATURE_KEYS.EXPENSES,
  ],
};

/**
 * Returns true if a role has a strict feature restriction map.
 * Managers, Admins, and unmapped roles return false (see everything).
 */
export const isRestrictedRole = (roleName: string): boolean => {
  if (!roleName) return false;
  // Admin/Manager roles are never restricted
  const freeRoles = ['Administrator', 'Manager', 'HRM Manager', 'Bar Manager', 'Restaurant Manager',
    'Shop Manager', 'Store Manager', 'Supermarket Manager', 'Hospital Administrator',
    'Chief Pharmacist', 'Super Admin'];
  if (freeRoles.some(r => roleName.toLowerCase().includes(r.toLowerCase()))) return false;
  
  // Case-insensitive check against ROLE_FEATURE_MAP keys
  return Object.keys(ROLE_FEATURE_MAP).some(
    key => key.toLowerCase() === roleName.toLowerCase()
  );
};

/**
 * Returns the restricted feature key set for a role, or null if no restriction.
 */
export const getRoleAllowedFeatures = (roleName: string): Set<number> | null => {
  if (!isRestrictedRole(roleName)) return null;

  // Find key case-insensitively
  const matchingKey = Object.keys(ROLE_FEATURE_MAP).find(
    key => key.toLowerCase() === roleName.toLowerCase()
  );
  if (!matchingKey) return null;

  const features = ROLE_FEATURE_MAP[matchingKey];
  if (!features || features.length === 0) return null;
  return new Set(features);
};

// Strict mapping of allowed features per business type (BUSINESS-SPECIFIC ONLY)
const TYPE_FEATURES: Record<BusinessType, number[]> = {
  hospital: [
    FEATURE_KEYS.HOSPITAL_DASHBOARD, FEATURE_KEYS.CLINICAL_QUEUE, FEATURE_KEYS.TRIAGE, FEATURE_KEYS.CONSULTATION,
    FEATURE_KEYS.PATIENTS, FEATURE_KEYS.MEDICATIONS, FEATURE_KEYS.PRESCRIPTIONS, FEATURE_KEYS.PHARMACY_POS,
    FEATURE_KEYS.PHARMACY_REPORTS, FEATURE_KEYS.HOSPITAL_LABS, FEATURE_KEYS.HOSPITAL_WARDS,
    FEATURE_KEYS.HOSPITAL_HR, FEATURE_KEYS.HOSPITAL_PAYROLL, FEATURE_KEYS.HOSPITAL_MANAGEMENT,
    FEATURE_KEYS.PHARMACY_DASHBOARD, FEATURE_KEYS.DISPENSING, FEATURE_KEYS.HRM, FEATURE_KEYS.PAYROLL
  ],
  pharmacy: [
    FEATURE_KEYS.PHARMACY_DASHBOARD, FEATURE_KEYS.PHARMACY_SALES, FEATURE_KEYS.PHARMACISTS, 
    FEATURE_KEYS.MEDICATIONS, FEATURE_KEYS.PRESCRIPTIONS, FEATURE_KEYS.PHARMACY_BATCHES, 
    FEATURE_KEYS.PHARMACY_REPORTS, FEATURE_KEYS.PHARMACY_PROCUREMENT, FEATURE_KEYS.PHARMACY_POS, 
    FEATURE_KEYS.DISPENSING, FEATURE_KEYS.PATIENTS, FEATURE_KEYS.PURCHASE_ORDERS,
    FEATURE_KEYS.DELIVERY_NOTES, FEATURE_KEYS.STOCK_MOVEMENTS, FEATURE_KEYS.SUPPLIERS,
    FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.CATEGORIES
  ],
  shoe: [
    FEATURE_KEYS.SHOE_DASHBOARD, FEATURE_KEYS.SHOE_CATALOG, FEATURE_KEYS.SHOE_POS,
    FEATURE_KEYS.SHOE_PREPACKS, FEATURE_KEYS.SHOE_AUDITS, FEATURE_KEYS.SHOE_MISMATCHED,
    FEATURE_KEYS.SHOE_CARE, FEATURE_KEYS.SHOE_LABELS, FEATURE_KEYS.SHOE_SIZE_CONVERTER,
    FEATURE_KEYS.CUSTOMERS, FEATURE_KEYS.DEBTS, FEATURE_KEYS.REPORTS,
    FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.SALES_HISTORY, FEATURE_KEYS.SUPPLIERS, FEATURE_KEYS.AUDIT_LOGS,
    FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.INVENTORY, FEATURE_KEYS.SALES, FEATURE_KEYS.POS_V2
  ],
  clothing: [
    FEATURE_KEYS.CLOTHING_DASHBOARD, FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.INVENTORY, FEATURE_KEYS.SALES, FEATURE_KEYS.POS_V2
  ],
  hardware: [
    FEATURE_KEYS.HARDWARE_DASHBOARD, FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.INVENTORY, FEATURE_KEYS.SALES, FEATURE_KEYS.POS_V2,
    FEATURE_KEYS.CUSTOMERS, FEATURE_KEYS.DEBTS, FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.SALES_HISTORY,
    FEATURE_KEYS.SUPPLIERS, FEATURE_KEYS.AUDIT_LOGS
  ],
  grocery: [
    FEATURE_KEYS.GROCERY_DASHBOARD, FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.INVENTORY, FEATURE_KEYS.SALES, FEATURE_KEYS.POS_V2
  ],
  jewelry: [
    FEATURE_KEYS.JEWELRY_DASHBOARD, FEATURE_KEYS.JEWELRY_VAULT, FEATURE_KEYS.JEWELRY_POS,
    FEATURE_KEYS.JEWELRY_TRADE_INS, FEATURE_KEYS.JEWELRY_SPOT_RATES, FEATURE_KEYS.JEWELRY_CERTIFICATES,
    FEATURE_KEYS.JEWELRY_REPORTS, FEATURE_KEYS.CUSTOMERS, FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS,
    FEATURE_KEYS.SALES_HISTORY, FEATURE_KEYS.SUPPLIERS, FEATURE_KEYS.AUDIT_LOGS,
    FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.INVENTORY, FEATURE_KEYS.SALES, FEATURE_KEYS.POS_V2
  ],
  hotel: [
    FEATURE_KEYS.HOTEL_DASHBOARD, FEATURE_KEYS.HOTEL_ROOMS, FEATURE_KEYS.HOTEL_RESERVATIONS,
    FEATURE_KEYS.HOTEL_HOUSEKEEPING, FEATURE_KEYS.HOTEL_FOLIOS, FEATURE_KEYS.HOTEL_REPORTS,
    FEATURE_KEYS.HOTEL_GUESTS, FEATURE_KEYS.EXPENSES, FEATURE_KEYS.REPORTS
  ],

  mobile_money: [
    FEATURE_KEYS.MOBILE_MONEY_DASHBOARD, FEATURE_KEYS.MOBILE_MONEY_POS, FEATURE_KEYS.MOBILE_MONEY_FLOATS,
    FEATURE_KEYS.MOBILE_MONEY_LEDGER, FEATURE_KEYS.MOBILE_MONEY_RECONCILIATION, FEATURE_KEYS.EXPENSES,
    FEATURE_KEYS.FUNDS, FEATURE_KEYS.REPORTS
  ],
  restaurant: [
    FEATURE_KEYS.RESTAURANT_DASHBOARD, FEATURE_KEYS.RESTAURANT_TABLES, FEATURE_KEYS.RESTAURANT_RESERVATIONS,
    FEATURE_KEYS.RESTAURANT_KITCHEN, FEATURE_KEYS.BAR_POS, FEATURE_KEYS.BAR_MENUS, FEATURE_KEYS.BAR_RECIPES,
    FEATURE_KEYS.BAR_TABS, FEATURE_KEYS.BAR_STOCK, FEATURE_KEYS.BAR_MOVEMENTS,
    FEATURE_KEYS.BAR_SHIFTS, FEATURE_KEYS.BAR_ANALYTICS, FEATURE_KEYS.RESTAURANT_REPORTS,
    FEATURE_KEYS.RESTAURANT_TABLE_TERMS, FEATURE_KEYS.COMPTOIRS
  ],
  coffee_shop: [
    FEATURE_KEYS.RESTAURANT_DASHBOARD, FEATURE_KEYS.RESTAURANT_TABLES, FEATURE_KEYS.RESTAURANT_RESERVATIONS,
    FEATURE_KEYS.RESTAURANT_KITCHEN, FEATURE_KEYS.BAR_POS, FEATURE_KEYS.BAR_MENUS, FEATURE_KEYS.BAR_RECIPES,
    FEATURE_KEYS.BAR_TABS, FEATURE_KEYS.BAR_STOCK, FEATURE_KEYS.BAR_MOVEMENTS,
    FEATURE_KEYS.BAR_SHIFTS, FEATURE_KEYS.BAR_ANALYTICS, FEATURE_KEYS.RESTAURANT_REPORTS,
    FEATURE_KEYS.RESTAURANT_TABLE_TERMS, FEATURE_KEYS.COMPTOIRS
  ],
  wholesale: [
    FEATURE_KEYS.DASHBOARD, FEATURE_KEYS.WHOLESALE, FEATURE_KEYS.SALES_HISTORY, FEATURE_KEYS.CUSTOMERS,
    FEATURE_KEYS.DEBTS, FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.STOCK, FEATURE_KEYS.STOCK_MOVEMENTS,
    FEATURE_KEYS.PURCHASE_ORDERS, FEATURE_KEYS.DELIVERY_NOTES, FEATURE_KEYS.SUPPLIERS, FEATURE_KEYS.REPORTS, FEATURE_KEYS.AUDIT_LOGS
  ],
  distributor: [
    FEATURE_KEYS.DELIVERY_NOTES, FEATURE_KEYS.PURCHASE_ORDERS, FEATURE_KEYS.SUPPLIERS, FEATURE_KEYS.WHOLESALE,
    FEATURE_KEYS.SALES_HISTORY, FEATURE_KEYS.CUSTOMERS, FEATURE_KEYS.DEBTS, FEATURE_KEYS.STOCK,
    FEATURE_KEYS.PRODUCTS, FEATURE_KEYS.STOCK_MOVEMENTS, FEATURE_KEYS.REPORTS, FEATURE_KEYS.AUDIT_LOGS
  ],
  bar: [
    FEATURE_KEYS.BAR_DASHBOARD, FEATURE_KEYS.BAR_POS, FEATURE_KEYS.BAR_STOCK, FEATURE_KEYS.BAR_MOVEMENTS,
    FEATURE_KEYS.BAR_RECIPES, FEATURE_KEYS.BAR_TABS, FEATURE_KEYS.BAR_SHIFTS, FEATURE_KEYS.BAR_ANALYTICS,
    FEATURE_KEYS.BAR_MENUS, FEATURE_KEYS.COMPTOIRS,
    FEATURE_KEYS.RESTAURANT_TABLES, FEATURE_KEYS.RESTAURANT_RESERVATIONS, FEATURE_KEYS.RESTAURANT_KITCHEN,
    FEATURE_KEYS.RESTAURANT_REPORTS, FEATURE_KEYS.RESTAURANT_TABLE_TERMS
  ],
  retail: [
    FEATURE_KEYS.RETAIL_DASHBOARD, FEATURE_KEYS.RETAIL_INVENTORY, FEATURE_KEYS.RETAIL_SUPPLIERS,
    FEATURE_KEYS.RETAIL_REPORTS, FEATURE_KEYS.WHOLESALE, FEATURE_KEYS.DELIVERY_NOTES
  ],
  salon: [
    FEATURE_KEYS.SALON_DASHBOARD, FEATURE_KEYS.SALON_APPOINTMENTS, FEATURE_KEYS.SALON_FORMULAS,
    FEATURE_KEYS.SALON_CHAIRS, FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.FINANCIAL_ANALYTICS
  ],
  barbershop: [
    FEATURE_KEYS.BARBERSHOP_DASHBOARD, FEATURE_KEYS.BARBERSHOP_POS, FEATURE_KEYS.BARBERSHOP_STATIONS,
    FEATURE_KEYS.BARBERSHOP_CATALOG, FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.FINANCIAL_ANALYTICS
  ],
  real_estate: [
    FEATURE_KEYS.REAL_ESTATE_DASHBOARD, FEATURE_KEYS.REAL_ESTATE_PROPERTIES, FEATURE_KEYS.REAL_ESTATE_UNITS,
    FEATURE_KEYS.REAL_ESTATE_RENT, FEATURE_KEYS.REAL_ESTATE_MAINTENANCE, FEATURE_KEYS.REPORTS,
    FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.FINANCIAL_ANALYTICS
  ],
  spa: [
    FEATURE_KEYS.SPA_DASHBOARD, FEATURE_KEYS.SPA_BOOKINGS, FEATURE_KEYS.SPA_ROOMS,
    FEATURE_KEYS.SPA_MEMBERSHIPS, FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.FINANCIAL_ANALYTICS
  ],
  tailoring: [
    FEATURE_KEYS.TAILORING_DASHBOARD, FEATURE_KEYS.TAILORING_ORDERS, FEATURE_KEYS.TAILORING_MEASUREMENTS,
    FEATURE_KEYS.TAILORING_FABRICS, FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.FINANCIAL_ANALYTICS
  ],
  car_rental: [
    FEATURE_KEYS.CAR_RENTAL_DASHBOARD, FEATURE_KEYS.CAR_RENTAL_FLEET, FEATURE_KEYS.CAR_RENTAL_AGREEMENTS,
    FEATURE_KEYS.CAR_RENTAL_DAMAGES, FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.FINANCIAL_ANALYTICS
  ],
  photography: [
    FEATURE_KEYS.RETAIL_DASHBOARD, FEATURE_KEYS.RETAIL_INVENTORY, FEATURE_KEYS.RETAIL_SUPPLIERS,
    FEATURE_KEYS.RETAIL_REPORTS, FEATURE_KEYS.WHOLESALE, FEATURE_KEYS.DELIVERY_NOTES
  ],
  freelancer: [
    FEATURE_KEYS.PROFESSIONAL_SERVICES_DASHBOARD, FEATURE_KEYS.PROFESSIONAL_SERVICES_ENGAGEMENTS,
    FEATURE_KEYS.PROFESSIONAL_SERVICES_TIMESHEETS, FEATURE_KEYS.PROFESSIONAL_SERVICES_RETAINERS,
    FEATURE_KEYS.REPORTS, FEATURE_KEYS.SALES_ANALYTICS, FEATURE_KEYS.FINANCIAL_ANALYTICS
  ],
  general: [
    FEATURE_KEYS.RETAIL_DASHBOARD, FEATURE_KEYS.RETAIL_INVENTORY, FEATURE_KEYS.RETAIL_SUPPLIERS,
    FEATURE_KEYS.RETAIL_REPORTS, FEATURE_KEYS.WHOLESALE, FEATURE_KEYS.DELIVERY_NOTES
  ]
};

export const isRoleVisible = (roleName: string, currentBusinessType: BusinessType, roleBusinessType?: string): boolean => {
  if (roleName === 'Super Admin') return true;

  if (currentBusinessType === 'hardware') {
    const hardwareRoles = ROLE_VISIBILITY.hardware;
    const isMatchedRole = hardwareRoles.some(r => r.toLowerCase() === roleName.toLowerCase());
    const isHardwareType = roleBusinessType?.toLowerCase() === 'hardware';
    return isMatchedRole || isHardwareType;
  }

  const dedicatedServiceTypes: BusinessType[] = [
    'real_estate', 'barbershop', 'spa', 'tailoring', 'freelancer', 'salon', 'car_rental', 'hotel', 'hospital', 'mobile_money'
  ];

  if (dedicatedServiceTypes.includes(currentBusinessType)) {
    const allowedRoles = ROLE_VISIBILITY[currentBusinessType] || [];
    const isMatchedRole = allowedRoles.some(r => r.toLowerCase() === roleName.toLowerCase());
    if (isMatchedRole) return true;
    if (roleBusinessType) {
      const normalizedRoleType = getBusinessType(roleBusinessType);
      if (normalizedRoleType === currentBusinessType) return true;
    }
    return false;
  }

  if (roleBusinessType) {
    const normalizedRoleType = getBusinessType(roleBusinessType);
    if (normalizedRoleType === 'general' || normalizedRoleType === currentBusinessType) {
      return true;
    }
  }

  const allowedRoles = ROLE_VISIBILITY[currentBusinessType];
  return allowedRoles?.includes(roleName) || false;
};

// Features that should be visible to ALL business types
const GLOBAL_FEATURES = [
  FEATURE_KEYS.DASHBOARD,
  FEATURE_KEYS.OWNER_DASHBOARD,
  FEATURE_KEYS.SETTINGS,
  FEATURE_KEYS.AUDIT_LOGS,
  FEATURE_KEYS.HRM,
  FEATURE_KEYS.PAYROLL,
  FEATURE_KEYS.INVENTORY,
  FEATURE_KEYS.PRODUCTS,
  FEATURE_KEYS.SALES,
  FEATURE_KEYS.DEBTS,
  FEATURE_KEYS.CUSTOMERS,
  FEATURE_KEYS.SUPPLIERS,
  FEATURE_KEYS.EXPENSES,
  FEATURE_KEYS.FUNDS,
  FEATURE_KEYS.POS_V2,
  FEATURE_KEYS.CATEGORIES,
  FEATURE_KEYS.STOCK_MOVEMENTS,
  FEATURE_KEYS.BRANCHS,
  // Granular Actions 
  FEATURE_KEYS.ADD_PRODUCT,
  FEATURE_KEYS.EDIT_PRODUCT,
  FEATURE_KEYS.ADD_STOCK,
  FEATURE_KEYS.EDIT_STOCK,
  FEATURE_KEYS.VIEW_COST_PRICE,
  FEATURE_KEYS.POS_DISCOUNT,
  FEATURE_KEYS.PROFITS
];

const SERVICE_VERTICAL_GLOBAL_FEATURES = [
  FEATURE_KEYS.DASHBOARD,
  FEATURE_KEYS.OWNER_DASHBOARD,
  FEATURE_KEYS.SETTINGS,
  FEATURE_KEYS.AUDIT_LOGS,
  FEATURE_KEYS.HRM,
  FEATURE_KEYS.PAYROLL,
  FEATURE_KEYS.DEBTS,
  FEATURE_KEYS.CUSTOMERS,
  FEATURE_KEYS.EXPENSES,
  FEATURE_KEYS.FUNDS,
  FEATURE_KEYS.PROFITS,
  FEATURE_KEYS.REPORTS,
  FEATURE_KEYS.BRANCHS,
  FEATURE_KEYS.SALES_ANALYTICS,
  FEATURE_KEYS.FINANCIAL_ANALYTICS,
];

export const isFeatureVisible = (featureName: string, featureKey: number, businessType: BusinessType): boolean => {
  const dedicatedServiceTypes: BusinessType[] = [
    'real_estate', 'barbershop', 'spa', 'tailoring', 'freelancer', 'salon', 'car_rental'
  ];
  if (dedicatedServiceTypes.includes(businessType)) {
    if (SERVICE_VERTICAL_GLOBAL_FEATURES.includes(featureKey as any)) {
      return true;
    }
    const allowedFeatures = TYPE_FEATURES[businessType];
    return allowedFeatures ? allowedFeatures.includes(featureKey) : false;
  }

  if ((GLOBAL_FEATURES as readonly number[]).includes(featureKey)) {
    return true;
  }
  const allowedFeatures = TYPE_FEATURES[businessType];
  if (allowedFeatures) {
    return allowedFeatures.includes(featureKey);
  }
  return true; 
};
